import React, {useCallback, useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {ADDRESS, GENDER} from '../../../../Common/Constants'
import {Nullable} from '../../../../Common/TypeHelper'
import {PhoneInformation} from '../../../Utils/Validations/PhoneInformation'
import {useAsync, useAsyncRetry} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PIMPrimaryButton} from '../../Atoms/Buttons/Bases/PIMPrimaryButton'
import {Spacer} from '../../../../Common/Components/Spacer'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMCardActions} from '../../Atoms/Layouts/PIMCardActions'
import {checkAsyncStateAndGetDataOrError, checkResponseAndGetDataOrError, isError, memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {PIMCard, PIMCardNoTopMessage} from '../../Atoms/Cards/PIMCard'
import {ColumnFlexContainer} from '../../Atoms/Containers'
import {MBContentBodyItemTypography, MBContentBodyTypography, MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {formatPostCode, PostCodeInformation} from '../../../../Common/Components/PostCodeInformation'
import {ShippingAddress, ShippingAddressInfo} from '../../../DTO/MemberShippingAddressDTO'
import {MemberNameConfirms} from '../../Components/ConfirmContentBlocks/MemberNameConfirms'
import {ConfirmContent} from '../../Molecules/BaseContentBlocks/ConfirmContent'
import {MBDeleteShippingAddressConfirmDialog} from '../../Molecules/MBDialogs'
import {MBLoadingOverlay} from '../../Molecules/MBLoadingOverlay'
import {useMBMemberErrorUnknown} from '../../Hooks/UseMBMemberError'
import {useTranslation} from 'react-i18next'

type MembersAccountInformationPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export const MembersAccountInformationPage: React.VFC<MembersAccountInformationPageProps> = (
  {
    navigator,
    memberRepo,
  },
): Nullable<JSX.Element> => {
  const {t} = useTranslation('member')
  const [openDialog, setOpenDialog] = useState(false)
  const [deleteShippingAddressInfo, setDeleteShippingAddressInfo] = useState<ShippingAddressInfo>()
  const [loadingOpen, setLoadingOpen] = useState(false)

  const throwMBMemberError = useMBMemberErrorUnknown()

  const resultGetShippingAddressList = useAsyncRetry(async () => {
    return await memberRepo.getShippingAddressList()
  })

  const goToShippingAddressInputPage = useCallback(() => {
    navigator.goToShippingAddressInputPage()
  }, [navigator])

  const goToEditMailPage = useCallback((): void => {
    navigator.goToEditMailPage()
  }, [navigator])

  const goToEditPasswordPage = useCallback((): void => {
    navigator.goToEditPasswordPage()
  }, [navigator])

  const goToEditAddressInputPage = useCallback((): void => {
    navigator.goToEditAddressInputPage()
  }, [navigator])

  const goToEditShippingAddressInputPage = useCallback((shippingAddressId: number): void => {
    navigator.goToEditShippingAddressInputPage(shippingAddressId)
  }, [navigator])

  const goToAccountTopPage = useCallback((): void => {
    navigator.goToAccountTopPage()
  }, [navigator])

  const handleDialogCancelButtonClick = useCallback((): void => {
    setOpenDialog(false)
  }, [openDialog])

  const handleDeleteShippingAddressButtonClick = useCallback((
    shippingAddressInfo: ShippingAddressInfo,
  ): void => {
    setOpenDialog(true)
    setDeleteShippingAddressInfo(shippingAddressInfo)
  }, [openDialog, deleteShippingAddressInfo])

  const handleDialogDeleteShippingAddressButtonClick = useCallback(async (
    shippingAddressId: number,
  ) => {
    setOpenDialog(false)
    setLoadingOpen(true)

    try {
      const resultDeleteShippingAddress = await memberRepo.deleteShippingAddress(shippingAddressId)
      const checkStatus = checkResponseAndGetDataOrError(resultDeleteShippingAddress)
      if (isError(checkStatus) && !(checkStatus.message === 'NotFound')) {
        // noinspection ExceptionCaughtLocallyJS
        throw checkStatus
      }

      resultGetShippingAddressList.retry()

      setLoadingOpen(false)
    } catch (e) {
      throwMBMemberError(e)
    }
  }, [memberRepo, resultGetShippingAddressList, throwMBMemberError])

  const result = useAsync(async () => {
    return await memberRepo.getSelf()
  })

  if (result.loading || resultGetShippingAddressList.loading) {
    return null
  }

  if (result.error) {
    throw new MBMemberError(result.error.message)
  }
  if (resultGetShippingAddressList.error) {
    throw new MBMemberError(resultGetShippingAddressList.error.message)
  }

  if (!result.value || !resultGetShippingAddressList.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(result.value.status)

  const shippingAddressInfoList = checkAsyncStateAndGetDataOrError(resultGetShippingAddressList)
  const getShippingAddressBlock = (): JSX.Element => {
    if (isError(shippingAddressInfoList)) {
      if (shippingAddressInfoList.message === 'Forbidden') return <></>
      else throw shippingAddressInfoList
    }

    if (shippingAddressInfoList.length > 0) {
      const shippingAddressInfo = shippingAddressInfoList[0]

      return <ShippingAddressConfirmBlock
        shippingAddress={shippingAddressInfo}
        onEditShippingAddressButtonClick={(): void =>
          goToEditShippingAddressInputPage(shippingAddressInfo.shippingAddressId)
        }
        onDeleteShippingAddressButtonClick={(): void =>
          handleDeleteShippingAddressButtonClick(shippingAddressInfo)
        }
      />
    }

    return <ShippingAddressRegisterBlock
      onRegisterButtonClick={goToShippingAddressInputPage}
    />
  }

  const {member} = result.value.data

  if (!member) {
    throw new MBMemberError('SystemError')
  }

  const {
    mail, familyName, firstName, familyNameKana, firstNameKana, gender, birthday, postCode,
    addressPrefectureCode, addressCity, addressStreet, addressBuilding, phoneMobile, phoneTel,
  } = member

  return (
    <>
      <MembersPageTemplate>
        <PageTitle>登録情報</PageTitle>

        <PIMStack
          alignItems={'center'}
          spacing={'18px'}
        >
          <PIMCardNoTopMessage
            headerProps={{title: 'ログイン情報'}}
            testId={'block-login-info'}
          >
            <PIMStack
              spacing={'18px'}
              divider={<MBContentBodyDivider/>}
              showEndDivider
            >
              <ConfirmContent label={t('登録情報.ログイン情報.ラベル メールアドレス')} values={[mail]}/>
            </PIMStack>

            <Spacer mt={'25px'}>
              <PIMCardActions>
                <PIMPrimaryButton onClick={goToEditMailPage}>
                  メールアドレス変更
                </PIMPrimaryButton>
                <PIMPrimaryButton onClick={goToEditPasswordPage}>
                  パスワード変更
                </PIMPrimaryButton>
              </PIMCardActions>
            </Spacer>
          </PIMCardNoTopMessage>

          <PIMCardNoTopMessage
            headerProps={{title: '基本情報'}}
            testId={'block-basic-info'}
          >
            <PIMStack
              spacing={'18px'}
              divider={<MBContentBodyDivider/>}
              showEndDivider
            >
              <MemberNameConfirms
                familyName={familyName}
                firstName={firstName}
                familyNameKana={familyNameKana}
                firstNameKana={firstNameKana}
              />
              <ConfirmContent label={'性別'} values={[gender ? GENDER[gender] : '']}/>
              <ConfirmContent label={'生年月日'} values={[birthday ?? '']}/>
            </PIMStack>
          </PIMCardNoTopMessage>

          <PIMCardNoTopMessage
            headerProps={{title: '連絡先情報'}}
            testId={'block-address-info'}
          >
            <PIMStack
              spacing={'18px'}
              divider={<MBContentBodyDivider/>}
              showEndDivider
            >
              <AddressInformation
                postCode={postCode}
                addressPrefectureCode={addressPrefectureCode}
                addressCity={addressCity}
                addressStreet={addressStreet}
                addressBuilding={addressBuilding}
              />
              <PIMStack spacing={'10px'}>
                <MBContentSubTitleTypography>{'電話番号'}</MBContentSubTitleTypography>
                <div>
                  <MBContentBodyTypography>
                    <PhoneInformation
                      mobile={phoneMobile}
                      home={phoneTel}
                    />
                  </MBContentBodyTypography>
                </div>
              </PIMStack>
            </PIMStack>

            <Spacer mt={'25px'}>
              <PIMPageActions>
                <PIMPrimaryButton onClick={goToEditAddressInputPage}>
                  連絡先情報変更
                </PIMPrimaryButton>
              </PIMPageActions>
            </Spacer>
          </PIMCardNoTopMessage>

          {
            getShippingAddressBlock()
          }

        </PIMStack>

        <Spacer mt={'30px'}>
          <PIMPageActions>
            <PIMSecondaryButton onClick={goToAccountTopPage}>
              {t('MYアカウント.ページ名')}に戻る
            </PIMSecondaryButton>
          </PIMPageActions>
        </Spacer>
      </MembersPageTemplate>

      <MBLoadingOverlay open={loadingOpen}/>
      {
        deleteShippingAddressInfo &&
          <MBDeleteShippingAddressConfirmDialog
              open={openDialog}
              shippingAddressInfo={
                `${formatPostCode(deleteShippingAddressInfo.postCode)}\n${getAddressString(
                  deleteShippingAddressInfo.addressPrefectureCode,
                  deleteShippingAddressInfo.addressCity,
                  deleteShippingAddressInfo.addressStreet,
                  deleteShippingAddressInfo.addressBuilding,
                )}`
              }
              onCancelButtonClick={handleDialogCancelButtonClick}
              onOkButtonClick={(): Promise<void> =>
                handleDialogDeleteShippingAddressButtonClick(
                  deleteShippingAddressInfo.shippingAddressId,
                )
              }
          />
      }
    </>
  )
}
const getAddressString = (
  addressPrefectureCode: string,
  addressCity?: string,
  addressStreet?: string,
  addressBuilding?: Nullable<string>,
): string => {
  const getAddressPrefecture =
    addressPrefectureCode ? ADDRESS.PREFECTURES[addressPrefectureCode] : ''
  const getAddressBuilding = addressBuilding ?? ''

  return `${getAddressPrefecture + addressCity + addressStreet} ${getAddressBuilding}`
}

type AddressInformationProps = {
  postCode: string
  addressPrefectureCode?: string
  addressCity?: string
  addressStreet?: string
  addressBuilding?: Nullable<string>
}
const AddressInformation: React.VFC<AddressInformationProps> = (
  {
    postCode,
    addressPrefectureCode,
    addressCity,
    addressStreet,
    addressBuilding,
  },
) => {
  if (addressPrefectureCode === undefined) {
    return <></>
  }

  return <PIMStack spacing={'10px'}>
    <MBContentSubTitleTypography>{'住所'}</MBContentSubTitleTypography>
    <ColumnFlexContainer>
      <MBContentBodyTypography><PostCodeInformation postCode={postCode}/></MBContentBodyTypography>
      <MBContentBodyTypography>
        {getAddressString(
          addressPrefectureCode,
          addressCity,
          addressStreet,
          addressBuilding,
        )}
      </MBContentBodyTypography>
    </ColumnFlexContainer>
  </PIMStack>
}

const shippingAddressBlockTitle = '商品配送先情報 (商品購入時のみ選択可)'

type ShippingAddressRegisterBlockProps = {
  onRegisterButtonClick: () => void
}
const ShippingAddressRegisterBlock: React.VFC<ShippingAddressRegisterBlockProps> = (
  {
    onRegisterButtonClick,
  },
) => {
  return (<>
    <PIMCard
      headerProps={{title: shippingAddressBlockTitle}}
      testId={'block-shipping-address-info'}
    >
      <PIMStack spacing={'18px'}>
        <MBContentBodyItemTypography>商品配送先のご登録はありません</MBContentBodyItemTypography>
        <ColumnFlexContainer alignItems={'flex-start'} gap={0.5}>
          <MBContentNoteWithMarkTypography note={'連絡先情報とは別の商品配送先を1件登録することができます。'}/>
          <MBContentNoteWithMarkTypography note={'商品ご注文の際に、レジ画面にてお届け先を選択してください。'}/>
        </ColumnFlexContainer>
      </PIMStack>

      <Spacer mt={'25px'}>
        <PIMPageActions>
          <PIMPrimaryButton onClick={onRegisterButtonClick}>
            商品配送先情報の登録
          </PIMPrimaryButton>
        </PIMPageActions>
      </Spacer>
    </PIMCard>
  </>)
}

type ShippingAddressConfirmBlockProps = {
  shippingAddress: ShippingAddress
  onEditShippingAddressButtonClick: () => void
  onDeleteShippingAddressButtonClick: () => void
}
const ShippingAddressConfirmBlock: React.VFC<ShippingAddressConfirmBlockProps> = (
  {
    shippingAddress,
    onEditShippingAddressButtonClick,
    onDeleteShippingAddressButtonClick,
  },
) => {
  const {
    familyName, firstName,
    familyNameKana, firstNameKana,
    postCode,
    addressPrefectureCode, addressCity, addressStreet, addressBuilding,
    phone,
  } = shippingAddress

  return (<>
    <PIMCardNoTopMessage
      headerProps={{title: shippingAddressBlockTitle}}
      testId={'block-shipping-address-info'}
    >
      <PIMStack
        spacing={'18px'}
        divider={<MBContentBodyDivider/>}
        showEndDivider
      >
        <MemberNameConfirms
          familyName={familyName}
          firstName={firstName}
          familyNameKana={familyNameKana}
          firstNameKana={firstNameKana}
        />
        <AddressInformation
          postCode={postCode}
          addressPrefectureCode={addressPrefectureCode}
          addressCity={addressCity}
          addressStreet={addressStreet}
          addressBuilding={addressBuilding}
        />
        <ConfirmContent label={'電話番号'} values={[phone]}/>
      </PIMStack>

      <Spacer mt={'25px'}>
        <PIMCardActions>
          <PIMPrimaryButton onClick={onEditShippingAddressButtonClick}>
            商品配送先情報変更
          </PIMPrimaryButton>
          <PIMPrimaryButton onClick={onDeleteShippingAddressButtonClick}>
            商品配送先情報削除
          </PIMPrimaryButton>
        </PIMCardActions>
      </Spacer>
    </PIMCardNoTopMessage>
  </>)
}
